/**
 * Campo de senha com botão de revelar.
 *
 * Existe como componente porque a plataforma tem cinco campos de senha em três
 * telas; um olho colado só no login deixaria os outros quatro sem ele.
 *
 * Decisões que não são óbvias:
 *
 * * **Revelar é sempre temporário.** O estado nasce oculto a cada montagem e
 *   nada o persiste. Senha visível esquecida na tela é o risco que o campo
 *   mascarado existe para evitar.
 * * **O botão é `aria-pressed`, não um ícone mudo.** Leitor de tela precisa
 *   anunciar se a senha está exposta — quem não enxerga a tela não tem como
 *   saber que alguém ao lado consegue ler.
 * * **Fica fora do `<label>`.** Um `<button>` dentro do label faria o clique
 *   também focar o campo em alguns navegadores, e o foco saltaria a cada
 *   alternância.
 */

import { useId, useState } from "react";

interface Props {
  rotulo: string;
  valor: string;
  onChange: (valor: string) => void;
  /** `current-password` no login; `new-password` ao definir uma nova. */
  autoComplete?: string;
  minLength?: number;
  required?: boolean;
  autoFocus?: boolean;
  /** Texto de apoio abaixo do campo — a regra de tamanho, por exemplo. */
  ajuda?: string;
}

export default function CampoSenha({
  rotulo,
  valor,
  onChange,
  autoComplete = "current-password",
  minLength,
  required = true,
  autoFocus = false,
  ajuda,
}: Props) {
  const [visivel, setVisivel] = useState(false);
  const id = useId();
  const idAjuda = ajuda ? `${id}-ajuda` : undefined;

  return (
    <div className="campo campo-senha">
      <label htmlFor={id}>{rotulo}</label>

      <div className="campo-senha-caixa">
        <input
          id={id}
          type={visivel ? "text" : "password"}
          value={valor}
          onChange={(e) => onChange(e.target.value)}
          autoComplete={autoComplete}
          minLength={minLength}
          required={required}
          autoFocus={autoFocus}
          aria-describedby={idAjuda}
        />
        <button
          type="button"
          className="campo-senha-olho"
          onClick={() => setVisivel((v) => !v)}
          aria-pressed={visivel}
          aria-label={visivel ? "Ocultar a senha" : "Mostrar a senha"}
          title={visivel ? "Ocultar a senha" : "Mostrar a senha"}
          // O botão não entra na navegação por Tab: quem usa teclado quer ir do
          // campo direto ao próximo, não parar num controle acessório.
          tabIndex={-1}
        >
          {visivel ? <IconeOlhoFechado /> : <IconeOlho />}
        </button>
      </div>

      {ajuda && (
        <small id={idAjuda} className="campo-ajuda">
          {ajuda}
        </small>
      )}
    </div>
  );
}

function IconeOlho() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M1.5 12S5 5.5 12 5.5 22.5 12 22.5 12 19 18.5 12 18.5 1.5 12 1.5 12Z"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.6" />
    </svg>
  );
}

function IconeOlhoFechado() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M3 3l18 18M10.6 10.7a2 2 0 002.8 2.8M9.4 5.9A9.6 9.6 0 0112 5.5c7 0 10.5 6.5 10.5 6.5a17 17 0 01-3.3 4.1M6.2 7.6A16.7 16.7 0 001.5 12S5 18.5 12 18.5c1 0 1.9-.1 2.7-.4"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
